import React, {
  createContext,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { func, node } from 'prop-types';

export const MenuFocusContext = createContext({
  focus: null,
  focusNext: null,
  focusPrevious: null,
  handleEnter: null,
  handleLeft: null,
  handleRight: null,
  register: null,
  selectedId: null,
  unregister: null,
});

const { Provider } = MenuFocusContext;

const MenuFocusProvider = forwardRef(({ children, onEnterKey, onLeftKey, onRightKey }, apiRef) => {
  const [selectedUid, setSelectedUid] = useState(null);

  const refsRef = useRef([]);

  const getRefByUid = useCallback(
    uid => refsRef.current.find(({ uid: refUid }) => refUid === uid),
    [],
  );

  const getRefIdIndex = useCallback(
    id => refsRef.current.findIndex(({ id: refId }) => refId === id),
    [],
  );

  const getRefUidIndex = useCallback(
    uid => refsRef.current.findIndex(({ uid: refUid }) => refUid === uid),
    [],
  );

  const register = useCallback(
    // "id" can be any value, is returned to the user in events, duplicates ok
    // "uid" must be a UNIQUE identifier and is used internally
    ({ id, uid, ref }) => {
      if (!refsRef.current.length) setSelectedUid(uid);
      refsRef.current.push({ id, uid, ref });
    },
    [],
  );

  const unregister = useCallback(
    uid => {
      const index = getRefUidIndex(uid);
      if (index > -1) refsRef.current.splice(index, 1);
    },
    [getRefUidIndex],
  );

  const focusIndex = useCallback(
    index => {
      const { uid, ref } = refsRef.current[index];
      setSelectedUid(uid);
      ref.current.focus();
    },
    [setSelectedUid],
  );

  const focus = useCallback(
    uid => {
      const index = getRefUidIndex(uid);
      if (index > -1) focusIndex(index);
    },
    [focusIndex, getRefUidIndex],
  );

  const focusFirst = useCallback(() => {
    if (refsRef.current.length) focusIndex(0);
  }, [focusIndex]);

  const focusId = useCallback(
    id => {
      const index = getRefIdIndex(id);
      if (index > -1) focusIndex(index);
    },
    [focusIndex, getRefIdIndex],
  );

  const focusNext = useCallback(
    uid => {
      const index = getRefUidIndex(uid);
      const nextIndex = index === refsRef.current.length - 1 ? 0 : index + 1;
      focusIndex(nextIndex);
    },
    [focusIndex, getRefUidIndex],
  );

  const focusPrevious = useCallback(
    uid => {
      const index = getRefUidIndex(uid);
      const nextIndex = index < 1 ? refsRef.current.length - 1 : index - 1;
      focusIndex(nextIndex);
    },
    [focusIndex, getRefUidIndex],
  );

  const handleLeft = useCallback(
    uid => onLeftKey && onLeftKey(getRefByUid(uid)?.id),
    [getRefByUid, onLeftKey],
  );

  const handleRight = useCallback(
    uid => onRightKey && onRightKey(getRefByUid(uid)?.id),
    [getRefByUid, onRightKey],
  );

  const handleEnter = useCallback(
    uid => onEnterKey && onEnterKey(getRefByUid(uid)?.id),
    [getRefByUid, onEnterKey],
  );

  const unfocus = useCallback(() => {
    if (selectedUid !== null) {
      const ref = getRefByUid(selectedUid)?.ref;
      ref?.current?.blur();
    }
  }, [getRefByUid, selectedUid]);

  const refocus = useCallback(
    delay => {
      const focusSelectedOrFirst = () => {
        if (selectedUid === null) focusFirst();
        else focus(selectedUid);
      };

      if (delay) setTimeout(focusSelectedOrFirst, delay);
      else focusSelectedOrFirst();
    },
    [focus, focusFirst, selectedUid],
  );

  useImperativeHandle(apiRef, () => ({
    focusFirst,
    focusId,
    refocus,
    unfocus,
  }));

  const value = useMemo(
    () => ({
      focus,
      focusFirst,
      focusNext,
      focusPrevious,
      handleEnter,
      handleLeft,
      handleRight,
      register,
      selectedUid,
      unregister,
    }),
    [
      focus,
      focusFirst,
      focusNext,
      focusPrevious,
      handleEnter,
      handleLeft,
      handleRight,
      register,
      selectedUid,
      unregister,
    ],
  );

  return <Provider value={value}>{children}</Provider>;
});

MenuFocusProvider.propTypes = {
  children: node.isRequired,
  onEnterKey: func,
  onLeftKey: func,
  onRightKey: func,
};

MenuFocusProvider.defaultProps = {
  onEnterKey: null,
  onLeftKey: null,
  onRightKey: null,
};

MenuFocusProvider.displayName = 'MenuFocusProvider';

export default MenuFocusProvider;
